import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import SectionedTaskSelector from './SectionedTaskSelector';

/**
 * Dialog for selecting tasks from sections
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to close the dialog
 * @param {Array} props.selectedTasks - Array of task IDs that are currently selected
 * @param {Function} props.onSave - Function to call when saving task selection
 * @param {string} props.title - Dialog title
 * @param {string} props.projectId - Project ID for context
 * @returns {React.ReactElement} Dialog component
 */
const TaskSelectorDialog = ({
  open,
  onClose,
  selectedTasks = [],
  onSave,
  title = "Select additional topics",
  projectId,
  existingProjectTasks = [] // New prop to know which tasks should be filtered out
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [localSelectedTasks, setLocalSelectedTasks] = useState([]); // Start with empty selection
  const [isSaving, setIsSaving] = useState(false);
  
  // Handle task selection
  const handleTaskSelection = (taskId) => {
    console.log('=== TASK SELECTION DEBUG ===');
    console.log('TaskSelectorDialog: handleTaskSelection called with taskId:', taskId);
    console.log('TaskSelectorDialog: Current selected tasks before update:', [...localSelectedTasks]);
    console.log('TaskSelectorDialog: Is task already selected?', localSelectedTasks.includes(taskId));
    
    setLocalSelectedTasks(prev => {
      console.log('Inside setState - prev value:', [...prev]);
      console.log('Inside setState - taskId to toggle:', taskId);
      console.log('Inside setState - is taskId in prev?', prev.includes(taskId));
      
      const newSelection = prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId];
      
      console.log('Inside setState - new selection will be:', newSelection);
      console.log('=== END TASK SELECTION DEBUG ===');
      return newSelection;
    });
  };
  
  // Handle save
  const handleSave = async () => {
    try {
      // Don't save if no tasks are selected
      if (!localSelectedTasks || localSelectedTasks.length === 0) {
        console.warn('No tasks selected, skipping save');
        return;
      }
      
      setIsSaving(true);
      if (onSave) {
        // Pass only the newly selected tasks
        const newlySelectedTasks = localSelectedTasks;
        console.log('Saving newly selected tasks:', newlySelectedTasks);
        await onSave(newlySelectedTasks);
      }
      onClose();
    } catch (error) {
      console.error('Error saving tasks:', error);
      // Re-throw to let parent handle the error
      throw error;
    } finally {
      setIsSaving(false);
    }
  };
  
  // Reset local selection when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      // When dialog opens, start with no selections for adding new topics
      console.log('TaskSelectorDialog: Dialog opened, resetting selections');
      setLocalSelectedTasks([]);
    }
  }, [open]);
  
  // Debug: Log state changes
  React.useEffect(() => {
    console.log('TaskSelectorDialog: localSelectedTasks changed to:', localSelectedTasks);
    console.log('TaskSelectorDialog: Number of selected tasks:', localSelectedTasks.length);
    
    // Check if all tasks got selected somehow
    if (localSelectedTasks.length > 1) {
      console.warn('Multiple tasks selected at once! This might indicate a bug.');
    }
  }, [localSelectedTasks]);
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '4px',
          overflow: 'hidden',
          margin: 'auto',
          maxWidth: '90vw',
          width: '90vw',
          maxHeight: '90vh',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle
         sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          pl: 6,
          pr: 5,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 500, fontSize: '1.125rem' }}>
          {title}
        </Typography>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={onClose} 
          aria-label="close"
          sx={{ mr: 0 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{
        p: 0,
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          marginTop: '5px',
          marginBottom: '5px',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.4)',
          },
        },
        '&::-webkit-scrollbar-corner': {
          backgroundColor: 'transparent',
        },
      }}>
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <SectionedTaskSelector
            selectedTasks={localSelectedTasks}
            onTaskSelection={handleTaskSelection}
            mode="selection"
            showSaveButton={false}
            showSkipButton={false}
            hideEmptySections={true}
            isDialog={true}
            projectId={projectId} // Pass projectId to enable orchestrator editing
            existingProjectTasks={existingProjectTasks || []} // Use the existing project tasks prop, not the selectedTasks
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        position: 'relative',
        p: 2,
        pr: 3,
        pl: 3,
        justifyContent: 'flex-end',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 24, // Match horizontal padding
          right: 24, // Match horizontal padding
          height: '1px',
          backgroundColor: (theme) => theme.palette.divider
        }
      }}>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || localSelectedTasks.length === 0}
          sx={{
            bgcolor: 'var(--color-blue)',
            color: 'white',
            '&:hover': {
              bgcolor: 'var(--color-blue-dark)',
            },
            minWidth: '120px',
            textTransform: 'none !important'
          }}
        >
          {isSaving ? 'Adding...' : 
           localSelectedTasks.length > 0 
            ? `Add ${localSelectedTasks.length} topic${localSelectedTasks.length > 1 ? 's' : ''}`
            : 'Select topics'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskSelectorDialog;